import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SCAEnrollmentState } from '__generated__/globalTypes';
import { Checkbox } from 'atoms/inputs/Checkbox';
import { NativeSelect } from 'atoms/inputs/NativeSelect';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, HeadlineL, LabelM, LabelS } from 'atoms/typography';
import { IntlDate } from 'components/IntlDate';
import type { GraphQLResult } from 'components/form/Form';
import { GraphqlForm, TextField } from 'components/form/Form';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useIntlCountries } from 'hooks/useIntlCountries';
import type {
  CreateFiatWalletArgs,
  CreateResultType,
} from 'hooks/wallets/useCreateOrUpdateFiatWallet';
import { useCreateOrUpdateFiatWallet } from 'hooks/wallets/useCreateOrUpdateFiatWallet';
import { fiatWallet, glossary, userAttributes } from 'lib/glossary';

import { EditableRow } from '../EditableRow';
import { StepWrapper } from '../StepWrapper';
import { MANGOPAY_PRIVACY_POLICY, MANGOPAY_TERMS } from '../externalLinks';
import { CreateFiatWalletSteps } from '../type';
import { useCurrencyOptions } from '../useCurrencyOptions';
import { useIsNotMajor } from '../useIsNotMajor';

const Content = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const Label = styled(BodyM)`
  color: var(--c-white);
`;

const CheckboxWrapper = styled.div`
  border-top: 1px solid var(--c-nd-200);
  padding-top: var(--double-unit);
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

type DeclarativeFormArgs = Omit<
  CreateFiatWalletArgs,
  'mangopayTermsAndConditionsAccepted' | 'dob'
> & { dob: Date };

type Props = {
  setStep: (step: CreateFiatWalletSteps) => void;
  args: DeclarativeFormArgs;
  onChange: (a: DeclarativeFormArgs) => void;
};

export const ReviewInfoBeforeAddingId = ({
  setStep,
  args,
  onChange: doOnChange,
}: Props) => {
  const {
    flags: { enableMangopaySca = false },
  } = useFeatureFlags();
  const countriesOfResidence = useIntlCountries(true);
  const nationalities = useIntlCountries(true);
  const currencies = useCurrencyOptions();
  const { create } = useCreateOrUpdateFiatWallet();
  const [declarativeFormArgs, setDeclarativeFormArgs] =
    useState<DeclarativeFormArgs>(args);

  const [
    mangopayTermsAndConditionsAccepted,
    setMangopayTermsAndConditionsAccepted,
  ] = useState<boolean>(false);

  const {
    firstName,
    lastName,
    countryOfResidenceCode,
    nationalityCode,
    dob,
    currency,
  } = declarativeFormArgs;

  const submitAndCreateFiatWallet = async (
    onResult: (result: GraphQLResult) => void
  ) => {
    const createResult = await create({
      ...declarativeFormArgs,
      dob,
      mangopayTermsAndConditionsAccepted,
    });
    onResult(createResult);
  };

  const isNotMajor = useIsNotMajor(dob);

  const onChange =
    (key: keyof DeclarativeFormArgs) => (value: string | Date) => {
      setDeclarativeFormArgs((prev: DeclarativeFormArgs) => ({
        ...prev,
        [key]: value,
      }));
      doOnChange({ ...declarativeFormArgs, [key]: value });
    };

  const onSuccess = (result: CreateResultType) => {
    if (
      enableMangopaySca &&
      'currentUser' in result &&
      result.currentUser?.mangopayUser?.scaEnrollmentState ===
        SCAEnrollmentState.SCA_PENDING_CONFIRMATION
    ) {
      setStep(CreateFiatWalletSteps.MANGOPAY_SCA);
    } else {
      setStep(CreateFiatWalletSteps.ACTIVATION_SUCCESS);
    }
  };

  const disabled =
    isNotMajor ||
    firstName === '' ||
    lastName === '' ||
    countryOfResidenceCode === '' ||
    nationalityCode === '' ||
    !currency ||
    !mangopayTermsAndConditionsAccepted;
  return (
    <StyledGraphqlForm
      onSubmit={(variables, onResult) => {
        submitAndCreateFiatWallet(onResult);
      }}
      onSuccess={onSuccess}
      render={(Error, SubmitButton) => (
        <StepWrapper
          setStep={setStep}
          step={CreateFiatWalletSteps.REVIEW_INFORMATION}
          submitButton={
            <SubmitButton size="medium" disabled={disabled}>
              <FormattedMessage {...fiatWallet.activateCashWallet} />
            </SubmitButton>
          }
        >
          <>
            <HeadlineL as="h3">
              <FormattedMessage
                id="CreateFiatWallet.reviewInformation.title"
                defaultMessage="Review your information"
              />
            </HeadlineL>
            <BodyM>
              <FormattedMessage
                id="CreateFiatWallet.reviewInformation.description"
                defaultMessage="You're almost done! Please review your information below for accuracy."
              />
            </BodyM>
            <Content>
              <Vertical gap={0}>
                <EditableRow
                  value={
                    countriesOfResidence.find(
                      c => c.value === countryOfResidenceCode
                    )?.label || ''
                  }
                  label={
                    <Label>
                      <FormattedMessage {...glossary.countryOfResidence} />
                    </Label>
                  }
                >
                  <NativeSelect
                    values={countriesOfResidence}
                    value={countryOfResidenceCode || ''}
                    name="countryOfResidence"
                    fullWidth
                    onChange={onChange('countryOfResidenceCode')}
                  />
                </EditableRow>
                <EditableRow
                  value={
                    nationalities.find(n => n.value === nationalityCode)
                      ?.label || ''
                  }
                  label={
                    <Label>
                      <FormattedMessage {...glossary.nationality} />
                    </Label>
                  }
                >
                  <NativeSelect
                    values={nationalities}
                    value={nationalityCode || ''}
                    name="nationality"
                    fullWidth
                    onChange={onChange('nationalityCode')}
                  />
                </EditableRow>
                <EditableRow
                  value={firstName}
                  label={
                    <Label>
                      <FormattedMessage {...glossary.firstName} />
                    </Label>
                  }
                >
                  <TextField
                    autoFocus
                    name="firstName"
                    defaultValue={firstName}
                    onChange={event =>
                      onChange('firstName')(event.target.value)
                    }
                  />
                </EditableRow>
                <EditableRow
                  value={lastName}
                  label={
                    <Label>
                      <FormattedMessage {...glossary.lastName} />
                    </Label>
                  }
                >
                  <TextField
                    autoFocus
                    name="lastName"
                    onChange={event => onChange('lastName')(event.target.value)}
                    defaultValue={lastName}
                  />
                </EditableRow>
                <EditableRow
                  value={
                    <FormattedDate
                      value={dob}
                      day="numeric"
                      month="long"
                      year="numeric"
                      timeZone="UTC"
                    />
                  }
                  label={
                    <Label>
                      <FormattedMessage {...glossary.dateOfBirth} />
                    </Label>
                  }
                >
                  <IntlDate value={dob} onChange={onChange('dob')} />
                </EditableRow>
                {!!isNotMajor && (
                  <LabelM color="var(--c-red-400)">
                    <FormattedMessage {...glossary.notMajorError} />
                  </LabelM>
                )}
                <EditableRow
                  value={currency}
                  label={
                    <Label>
                      <FormattedMessage {...userAttributes.fiatCurrency} />
                    </Label>
                  }
                >
                  <NativeSelect
                    fullWidth
                    name="currency"
                    values={currencies}
                    value={currency ?? ''}
                    onChange={onChange('currency')}
                  />
                </EditableRow>
                <CheckboxWrapper>
                  <Checkbox
                    name="mangopayTermsAndConditionsAccepted"
                    noPadding
                    checked={mangopayTermsAndConditionsAccepted}
                    onChange={e => {
                      setMangopayTermsAndConditionsAccepted(e.target.checked);
                    }}
                    label={
                      <LabelS color="var(--c-white)">
                        <FormattedMessage
                          id="createFiatWallet.chooseCurrency.terms"
                          defaultMessage="I agree to Mangopay’s <linkTerms>Terms and Conditions</linkTerms> and that my personal data will be processed pursuant to the <linkPrivacy>Privacy Policy</linkPrivacy>."
                          values={{
                            linkTerms: children => (
                              <StyledLink href={MANGOPAY_TERMS} target="_blank">
                                {children}
                              </StyledLink>
                            ),
                            linkPrivacy: children => (
                              <StyledLink
                                href={MANGOPAY_PRIVACY_POLICY}
                                target="_blank"
                              >
                                {children}
                              </StyledLink>
                            ),
                          }}
                        />
                      </LabelS>
                    }
                  />
                </CheckboxWrapper>
              </Vertical>
              <Error />
            </Content>
          </>
        </StepWrapper>
      )}
    />
  );
};
